import {Role} from "@core/model";
import {
  AuditIcon,
  TicketIcon,
  GroupIcon,
  UserIcon,
  AssetIcon,
  ArchiveIcon,
  MonitoringIcon,
  TaskIcon,
  DocumentIcon,
  PrognosisIcon,
  DiagnosisIcon,
  DashboardIcon,
  ArtifactIcon,
  CustomerIcon,
  TagIcon,
  GoodsIcon,
  CallIcon,
  ChecklistIcon,
} from "@components/icons";
import {AdminPanelSettings} from "@mui/icons-material";
import {TenantMenuItem, tenantMenuItemType} from "@core/model/Tenant";
import {Stack, Typography} from "@mui/material";
import {canManageArtifacts} from "@util";

type DrawerItem = {
  icon: JSX.Element;
  name: string;
  i18n: string;
  path: string;
};

const drawerItems: {
  [key in tenantMenuItemType]: DrawerItem;
} = {
  SEPARATOR: {
    icon: <></>,
    name: "separator",
    i18n: "",
    path: "",
  },
  HTML: {
    icon: <></>,
    name: "html",
    i18n: "",
    path: "",
  },
  DASHBOARD: {
    icon: <DashboardIcon />,
    name: "dashboard",
    i18n: "drawer.dashboard",
    path: "/dashboard/home",
  },
  ASSETS: {
    icon: <AssetIcon />,
    name: "assets",
    i18n: "drawer.assets",
    path: "/dashboard/assets",
  },
  TICKETS: {
    icon: <TicketIcon />,
    name: "tickets",
    i18n: "drawer.tickets",
    path: "/dashboard/tickets",
  },
  TASKS: {
    icon: <TaskIcon />,
    name: "tasks",
    i18n: "drawer.tasks",
    path: "/dashboard/tasks/month",
  },
  ARTIFACTS: {
    icon: <ArtifactIcon />,
    name: "artifacts",
    i18n: "drawer.artifacts",
    path: "/dashboard/artifacts",
  },
  DOCUMENTS: {
    icon: <DocumentIcon />,
    name: "documents",
    i18n: "drawer.documents",
    path: "/dashboard/documents",
  },
  ARCHIVE: {
    icon: <ArchiveIcon />,
    name: "archive",
    i18n: "drawer.archive",
    path: "/dashboard/archive",
  },
  MONITORING: {
    icon: <MonitoringIcon />,
    name: "live",
    i18n: "drawer.monitoring",
    path: "/dashboard/monitoring",
  },
  PROGNOSIS: {
    icon: <PrognosisIcon />,
    name: "prognosis",
    i18n: "drawer.prognosis",
    path: "/dashboard/prognosis",
  },
  DIAGNOSIS: {
    icon: <DiagnosisIcon />,
    name: "diagnosis",
    i18n: "drawer.diagnosis",
    path: "/dashboard/diagnosis",
  },
  GROUPS: {
    icon: <GroupIcon />,
    name: "groups",
    i18n: "drawer.groups",
    path: "/dashboard/groups",
  },
  AUDITS: {
    icon: <AuditIcon />,
    name: "audits",
    i18n: "drawer.audits",
    path: "/dashboard/audits",
  },
  USERS: {
    icon: <UserIcon />,
    name: "users",
    i18n: "drawer.users",
    path: "/dashboard/users",
  },
  TAGS: {
    icon: <TagIcon />,
    name: "tags",
    i18n: "drawer.tags",
    path: "/dashboard/tags",
  },
  GOODS: {
    icon: <GoodsIcon />,
    name: "goods",
    i18n: "drawer.goods",
    path: "/dashboard/goods",
  },
  CALLS: {
    icon: <CallIcon />,
    name: "calls",
    i18n: "drawer.calls",
    path: "/dashboard/calls",
  },
};

const filteredDrawerItems = (menuItem: TenantMenuItem, role: Role) => {
  return (
    ["GROUPS", "USERS"].includes(menuItem.type) &&
    !(role === "ADMIN" || role === "TENANT_ADMIN")
  );
};

const getDrawerItems = (
  role: Role,
  tenantMenu: TenantMenuItem[] | null,
): (DrawerItem | JSX.Element)[] => {
  const tenantItems =
    tenantMenu
      ?.map((menuItem, index) => {
        if (filteredDrawerItems(menuItem, role)) {
          return null;
        }

        if (menuItem.type === "SEPARATOR") {
          return (
            <Stack key={`drawer_subheader_${index}`} className="drawer-section">
              <Typography color="primary" variant="body2" fontWeight={"bold"}>
                {menuItem.label ?? "Gruppo"}
              </Typography>
            </Stack>
          );
        }
        if (menuItem.type === "HTML") {
          return (
            <Stack
              key={`drawer_html_${index}`}
              direction={"row"}
              gap={2}
              mx={2}
              my={1}
              dangerouslySetInnerHTML={{__html: menuItem.label ?? ""}}></Stack>
          );
        }

        // chi non gestisce le produzioni vi compila solo le checklist dei task assegnati
        if (menuItem.type === "ARTIFACTS" && !canManageArtifacts(role)) {
          return {
            ...drawerItems.ARTIFACTS,
            icon: <ChecklistIcon />,
            i18n: "drawer.checklist",
          };
        }

        return drawerItems[menuItem.type];
      })
      .filter(item => item !== null) ?? [];

  const adminItem = {
    icon: <AdminPanelSettings />,
    name: "admin",
    i18n: "drawer.admin",
    path: "/dashboard/admin",
  };

  return [
    ...(role === "ADMIN"
      ? [
          {
            icon: <CustomerIcon />,
            name: "tenants",
            i18n: "drawer.tenants",
            path: "/dashboard/tenants",
          },
        ]
      : []),
    ...tenantItems,
    ...(role === "ADMIN" || role === "TENANT_ADMIN" ? [adminItem] : []),
  ] as (DrawerItem | JSX.Element)[];
};

export default getDrawerItems;
